import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { skipToken } from '@reduxjs/toolkit/query';
import { FileChangeImpactModal, LinkedFile } from '../FileChangeImpactModal';
import {
  EditFormSkeleton,
  PartySectionWrapper,
  ContractDetailsSection,
} from './EditFileModalSections';
import type {
  PartyState,
  SignaturePayloadItem,
  EditFileModalProps,
  OriginalFormData,
} from './types';
import './EditFileModal.scss';
import { useTranslation } from 'react-i18next';

// ─── Helpers ────────────────────────────────────────────────────────────────

const MIN_PARTIES = 2;

/** Map raw API party data into local PartyState */
const mapPartiesToState = (rawParties: any[]): PartyState[] => {
  const mapped: PartyState[] = rawParties.map((p: any) => ({
    name: p.name || '',
    role: p.role || '',
    signed: p.signed || false,
    party_id: p.party_id || '',
  }));

  // Ensure minimum 2 parties are always present
  while (mapped.length < MIN_PARTIES) {
    mapped.push({ name: '', role: '', signed: false, party_id: '' });
  }

  return mapped;
};

/**
 * Signatures live at structured_data.signatures — not on the parties array.
 * Enable the checkbox only when at least one party has party_signed: true.
 */
const hasSignatureData = (rawSignatures: any[]): boolean =>
  Array.isArray(rawSignatures) &&
  rawSignatures.some((s) => s.party_signed === true);

/**
 * Merge structured_data.signatures into party states by matching party_id.
 * Uses party_signed as the authoritative signed field.
 */
const mergeSignaturesIntoParties = (
  parties: PartyState[],
  rawSignatures: any[]
): PartyState[] => {
  if (!rawSignatures.length) return parties;

  const signatureMap = new Map(rawSignatures.map((s: any) => [s.party_id, s]));

  return parties.map((party) => {
    const sig = signatureMap.get(party.party_id);
    return sig
      ? { ...party, signed: sig.party_signed ?? sig.signed ?? false }
      : party;
  });
};

/** Build the signatures payload array */
const buildSignaturesPayload = (
  parties: PartyState[],
  signingDate: string
): SignaturePayloadItem[] =>
  parties
    .filter((p) => p.party_id)
    .map((p) => ({
      party_id: p.party_id,
      party_signed: p.signed,
      signed_on: p.signed ? signingDate : '',
    }));

// ─── Component ──────────────────────────────────────────────────────────────

const EditFileModal: React.FC<EditFileModalProps> = ({
  open,
  onClose,
  onSave,
  fileId,
  useGetFileDataQuery,
  useEditFileMutation,
  useGetRelatedFilesQuery,
}) => {
  const { t } = useTranslation();
  // ── Form state ──────────────────────────────────────────────────────────
  const [parties, setParties] = useState<PartyState[]>([]);
  const [filename, setFilename] = useState('');
  const [title, setTitle] = useState('');
  const [contractDate, setContractDate] = useState('');
  const [documentSigned, setDocumentSigned] = useState(false);
  const [signingDate, setSigningDate] = useState('');
  const [originalData, setOriginalData] = useState<OriginalFormData | null>(
    null
  );

  // ── UI state ────────────────────────────────────────────────────────────
  const [error, setError] = useState<string | null>(null);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(true);
  const [pendingPayload, setPendingPayload] = useState<Record<
    string,
    any
  > | null>(null);

  // ── API hooks ───────────────────────────────────────────────────────────
  const [editFile, { isLoading: isSaving }] = useEditFileMutation();

  const { data, isLoading: isLoadingFileData } = useGetFileDataQuery(
    fileId ?? skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const { data: relatedFilesData } = useGetRelatedFilesQuery(
    fileId ? parseInt(fileId) : skipToken,
    { skip: !fileId || !open }
  );

  const fileData = data?.data;

  // ── Derived / memoized values ───────────────────────────────────────────

  const partyChanged = useMemo(() => {
    if (!originalData) return false;
    if (parties.length !== originalData.parties.length) return true;
    return parties.some(
      (p, i) => p.name !== (originalData.parties[i]?.name ?? '')
    );
  }, [parties, originalData]);

  const signatureChanged = useMemo(() => {
    if (!originalData) return false;
    if (documentSigned !== originalData.documentSigned) return true;
    if (signingDate !== originalData.signingDate) return true;
    return parties.some(
      (p, i) => p.signed !== (originalData.parties[i]?.signed ?? false)
    );
  }, [parties, documentSigned, signingDate, originalData]);

  const titleChanged = useMemo(() => {
    if (!originalData) return false;
    return title !== originalData.title;
  }, [title, originalData]);

  const changeContextLabel = useMemo(() => {
    const labels: string[] = [];
    if (partyChanged) labels.push('parties');
    if (titleChanged) labels.push('title');
    return labels.length > 0 ? labels.join(' and ') : 'changes';
  }, [partyChanged, titleChanged]);

  const linkedFiles = useMemo<LinkedFile[]>(() => {
    if (!relatedFilesData?.related_files) return [];
    return relatedFilesData.related_files.map(
      (f: { id: number; filename: string }) => ({
        id: f.id,
        filename: f.filename,
      })
    );
  }, [relatedFilesData]);

  // ── Initialize form from API data ──────────────────────────────────────

  useEffect(() => {
    if (!open || !fileData) return;

    const rawParties = fileData?.structured_data?.parties || [];
    const partiesArray = Array.isArray(rawParties) ? rawParties : [];
    const mappedParties = mapPartiesToState(partiesArray);

    // Signatures live at structured_data.signatures — merge into parties by party_id
    const rawSignatures = fileData?.structured_data?.signatures || [];
    const initialParties = mergeSignaturesIntoParties(
      mappedParties,
      rawSignatures
    );

    const initialFilename = fileData?.filename || '';
    const initialTitle =
      fileData?.metadata?.parent_contract_name ||
      fileData?.structured_data?.title ||
      '';
    const initialContractDate = fileData?.contract_date || '';

    // Only enable the checkbox when the signatures array is non-empty
    const initialDocumentSigned = hasSignatureData(rawSignatures);
    const initialSigningDate =
      rawSignatures.find((s: any) => s.signed_on)?.signed_on ||
      fileData?.signed_on ||
      fileData?.signing_date ||
      '';

    setParties(initialParties);
    setFilename(initialFilename);
    setTitle(initialTitle);
    setContractDate(initialContractDate);
    setDocumentSigned(initialDocumentSigned);
    setSigningDate(initialSigningDate);

    setOriginalData({
      parties: initialParties,
      filename: initialFilename,
      title: initialTitle,
      contractDate: initialContractDate,
      documentSigned: initialDocumentSigned,
      signingDate: initialSigningDate,
    });

    setError(null);
    setSignatureError(null);
    setShowEditDialog(true);
  }, [open, fileData]);

  // ── Handlers ───────────────────────────────────────────────────────────

  const handlePartyNameChange = useCallback((index: number, value: string) => {
    setParties((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], name: value };
      return updated;
    });
  }, []);

  const handleTogglePartySigned = useCallback((index: number) => {
    setSignatureError(null);
    setParties((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], signed: !updated[index].signed };
      return updated;
    });
  }, []);

  const handleDocumentSignedChange = useCallback((checked: boolean) => {
    setDocumentSigned(checked);
    setSignatureError(null);
    if (!checked) {
      setSigningDate('');
      setParties((prev) => prev.map((p) => ({ ...p, signed: false })));
    }
  }, []);

  const handleClose = useCallback(() => {
    setParties([]);
    setFilename('');
    setTitle('');
    setContractDate('');
    setDocumentSigned(false);
    setSigningDate('');
    setOriginalData(null);
    setError(null);
    setSignatureError(null);
    setShowConfirmModal(false);
    setShowEditDialog(true);
    setPendingPayload(null);
    onClose();
  }, [onClose]);

  // ── Validation ─────────────────────────────────────────────────────────

  const validateFields = useCallback(() => {
    if (!filename.trim()) return setError('Filename is required'), false;
    if (!title.trim()) return setError('Title is required'), false;
    if (contractDate && !/^\d{4}-\d{2}-\d{2}$/.test(contractDate))
      return setError('Invalid date format (YYYY-MM-DD required)'), false;
    if (parties.length < MIN_PARTIES)
      return setError('At least 2 parties are required'), false;
    if (!parties[0]?.name?.trim() || !parties[1]?.name?.trim())
      return setError('Both Party 1 and Party 2 must be filled'), false;
    if (documentSigned && !parties.some((p) => p.signed))
      return (
        setSignatureError('Please select at least one party as signed'), false
      );

    setSignatureError(null);
    setError(null);
    return true;
  }, [filename, title, contractDate, parties, documentSigned]);

  // ── Save ───────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validateFields() || !originalData || !fileId) return;

    const payload: Record<string, any> = { file_id: parseInt(fileId) };

    // Parties
    if (partyChanged) {
      payload.party_one = parties[0]?.name || '';
      payload.party_two = parties[1]?.name || '';
      parties.slice(2).forEach((party, i) => {
        payload[`party_${i + 3}`] = party.name || '';
      });
    }

    // Signatures — send as array: [{ party_id, party_signed, signed_on }]
    if (signatureChanged) {
      payload.signatures = buildSignaturesPayload(parties, signingDate);
    }

    // Title
    if (titleChanged) {
      payload.contract_title = title;
    }

    // Contract date & filename
    if (contractDate !== originalData.contractDate)
      payload.contract_date = contractDate;
    if (filename !== originalData.filename) payload.filename = filename;

    // No meaningful changes
    if (Object.keys(payload).length <= 1) {
      setError('No changes detected');
      return;
    }

    payload.edit_all = false;

    try {
      await editFile(payload).unwrap();
      onSave?.({ parties, filename, title, contractDate });

      setShowEditDialog(false);

      const hasImpactableChanges = partyChanged || titleChanged;
      if (hasImpactableChanges && linkedFiles.length > 0) {
        const changedFields: Record<string, any> = {};
        if (partyChanged) {
          changedFields.party_one = parties[0]?.name || '';
          changedFields.party_two = parties[1]?.name || '';
          parties.slice(2).forEach((party, i) => {
            changedFields[`party_${i + 3}`] = party.name || '';
          });
        }
        if (titleChanged) {
          changedFields.contract_title = title;
        }
        setPendingPayload(changedFields);
        setShowConfirmModal(true);
      } else {
        handleClose();
      }
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to save changes');
    }
  };

  const performBatchUpdate = async (
    fileIds: number[],
    changedFields: Record<string, any>
  ) => {
    try {
      await editFile({
        ...changedFields,
        files: fileIds,
        edit_all: false,
      }).unwrap();
      handleClose();
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to update related files');
    }
  };

  const handleConfirmImpact = async (
    selectedFileIds: number[],
    changedFields: Record<string, any>
  ) => {
    setShowConfirmModal(false);
    if (selectedFileIds.length > 0) {
      await performBatchUpdate(selectedFileIds, changedFields);
    } else {
      handleClose();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog
        open={open && showEditDialog}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className="edit-file-modal"
      >
        <DialogTitle className="edit-file-modal__title">
          <Typography variant="h6" fontWeight={700} color="text.primary">
            {t('Edit File Metadata')}
          </Typography>
          <IconButton
            aria-label={t('Close')}
            onClick={handleClose}
            disabled={isSaving}
            size="small"
            className="edit-file-modal__close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent className="edit-file-modal__content">
          {error && (
            <Box className="edit-file-modal__error">
              <ErrorOutlineIcon className="edit-file-modal__error-icon" />
              <Typography variant="caption">{error}</Typography>
            </Box>
          )}

          {isLoadingFileData ? (
            <EditFormSkeleton />
          ) : (
            <>
              <PartySectionWrapper
                parties={parties}
                onPartyNameChange={handlePartyNameChange}
                documentSigned={documentSigned}
                onDocumentSignedChange={handleDocumentSignedChange}
                onTogglePartySigned={handleTogglePartySigned}
                signingDate={signingDate}
                onSigningDateChange={setSigningDate}
                signatureError={signatureError}
              />

              <ContractDetailsSection
                title={title}
                onTitleChange={setTitle}
                filename={filename}
                onFilenameChange={setFilename}
                contractDate={contractDate}
                onContractDateChange={setContractDate}
              />
            </>
          )}
        </DialogContent>

        <DialogActions className="edit-file-modal__actions">
          <Button
            onClick={handleClose}
            disabled={isSaving || isLoadingFileData}
            className="edit-file-modal__btn-cancel"
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving || isLoadingFileData}
            className="edit-file-modal__btn-save"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <FileChangeImpactModal
        open={open && showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          handleClose();
        }}
        onConfirm={handleConfirmImpact}
        linkedFiles={linkedFiles}
        contextLabel={changeContextLabel}
        changedFields={pendingPayload || {}}
        isLoading={isSaving}
      />
    </>
  );
};

export { EditFileModal };
